import React, { useCallback, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Stack,
	Tooltip,
	Typography,
	alpha,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { GridCellEditStopReasons } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { paths } from '@/app/paths';
import useCellModes from '@/hooks/useCellModes';
import { AdminDataGrid } from '@/admin/shared/AdminDataGrid';
import { useAdminConfirm } from '@/admin/shared/AdminConfirmProvider';
import { useListAllBrokerGroupsQuery } from '@/admin/brokerGroups/api/brokerGroupsApi';
import { useListAllFeatureFlagsQuery } from '@/admin/featureFlags/api/featureFlagsApi';
import { useListInsightFieldsForLedgersQuery } from '@/admin/api/insightFieldsApi';
import { useListAllUsersQuery } from '../../api/usersApi';
import { useStartUserImportMutation } from '../../api/userImportsApi';
import { getClientConfigBrokerGroupIds } from '../../userValidationSchema';
import { IMPORT_ROW_STATUSES, checkRows, getImportRowEmail, getImportRowStatus } from './checkRows';
import { getUserImportReviewColumns } from './columns';
import { USER_IMPORT_FIELDS } from './importFields';
import { UserImportReviewSummary } from './UserImportReviewSummary';
import { useLeaveGuard } from './useLeaveGuard';

/**
 * The second step of a user import: the rows of the file read by readUserImport (`file`:
 * { storagePath, fileName, rows }), in an editable grid, each checked in the browser
 * (checkRows) as the admin fixes it. A cell with an error or warning is outlined, with the
 * message on hover.
 *
 * The admin can edit cells, select rows and delete them, show only rows with problems, and
 * remove the rows whose email is already a Folio user's (they'd be skipped). Import N users
 * starts the import (startUserImport) with the rows as they are now; it's disabled while any
 * row has an error or the checks are loading. If the backend's own check finds errors (e.g.
 * a user created since), they're shown on their rows until those rows are edited. Once it
 * starts, the admin goes to its report. Leaving before then asks first (useLeaveGuard);
 * Choose another file (onCancel) goes back to the upload.
 */
export const UserImportReview = ({ file, onCancel }) => {
	const navigate = useNavigate();
	const { cellModesModel, handleCellClick, handleCellModesModelChange } = useCellModes();
	const confirm = useAdminConfirm();
	const allowLeave = useLeaveGuard({
		title: 'Leave without importing?',
		content: 'Your changes to these rows will be lost.',
	});
	const [rows, setRows] = useState(file.rows);
	const [editedRowNumbers, setEditedRowNumbers] = useState(new Set());
	// Problems the backend found when the import was started, by row number
	const [serverProblems, setServerProblems] = useState({});
	const [selection, setSelection] = useState([]);

	const { data: brokerGroups, isLoading: areBrokerGroupsLoading } = useListAllBrokerGroupsQuery();
	const { data: users, isLoading: areUsersLoading } = useListAllUsersQuery();
	const { data: flags, isLoading: areFlagsLoading } = useListAllFeatureFlagsQuery();
	const [startUserImport, { isLoading: isStarting, error }] = useStartUserImportMutation();

	const brokerGroupsById = useMemo(
		() => new Map((brokerGroups ?? []).map((brokerGroup) => [brokerGroup.id, brokerGroup])),
		[brokerGroups],
	);
	const existingEmails = useMemo(
		() => new Set((users ?? []).map((user) => getImportRowEmail(user)).filter(Boolean)),
		[users],
	);
	const ledgers = useMemo(
		() =>
			[
				...new Set(
					rows
						.map(({ values }) => brokerGroupsById.get(values.brokerGroupId)?.ledger)
						.filter(Boolean),
				),
			].sort(),
		[rows, brokerGroupsById],
	);
	const { data: insightOptions, isFetching: isInsightFetching } =
		useListInsightFieldsForLedgersQuery(ledgers, { skip: !ledgers.length });
	const isChecking =
		areBrokerGroupsLoading || areUsersLoading || areFlagsLoading || isInsightFetching;

	const checkedRows = useMemo(
		() =>
			checkRows(rows, {
				brokerGroupsById,
				existingEmails,
				clientConfigBrokerGroupIds: getClientConfigBrokerGroupIds(flags ?? []),
				// Defaulted here, not in the query's destructuring, so the memo isn't given a
				// new {} on every render while there are no lists
				insightOptions: insightOptions ?? {},
			}).map((row) => {
				const problems = serverProblems[row.rowNumber];
				if (!problems) return row;
				const errors = { ...problems.errors, ...row.errors };
				const warnings = { ...problems.warnings, ...row.warnings };
				return {
					...row,
					errors,
					warnings,
					status: getImportRowStatus({ errors, warnings }),
				};
			}),
		[rows, brokerGroupsById, existingEmails, flags, insightOptions, serverProblems],
	);
	// Memoised, so the grid only gets new rows when the rows or their checks change (not when,
	// e.g., a row is ticked)
	const gridRows = useMemo(
		() =>
			checkedRows.map(({ rowNumber, values, status, errors, warnings }) => ({
				...values,
				rowNumber,
				status,
				errors,
				warnings,
			})),
		[checkedRows],
	);
	const errorRowCount = checkedRows.filter(
		({ status }) => status === IMPORT_ROW_STATUSES.error,
	).length;
	// Why Import can't start yet, shown on the disabled button; empty when it can
	const importDisabledReason =
		(!rows.length && 'There are no rows to import.') ||
		(isChecking && 'The rows are still being checked.') ||
		(errorRowCount &&
			`${errorRowCount} ${errorRowCount === 1 ? 'row has' : 'rows have'} errors. Fix or delete ${errorRowCount === 1 ? 'it' : 'them'} first.`) ||
		'';
	const existingUserRowNumbers = rows
		.filter(({ values }) => existingEmails.has(getImportRowEmail(values)))
		.map(({ rowNumber }) => rowNumber);

	const updateValues = useCallback((rowNumber, changes) => {
		setRows((previous) =>
			previous.map((row) =>
				row.rowNumber === rowNumber
					? { ...row, values: { ...row.values, ...changes } }
					: row,
			),
		);
		setEditedRowNumbers((previous) => new Set(previous).add(rowNumber));
		// Edited rows are checked again in the browser; the backend checks them when importing
		setServerProblems((previous) =>
			Object.fromEntries(
				Object.entries(previous).filter(
					([problemRowNumber]) => Number(problemRowNumber) !== rowNumber,
				),
			),
		);
	}, []);

	const columns = useMemo(
		() =>
			getUserImportReviewColumns({
				onFlagChange: (rowNumber, field, checked) =>
					updateValues(rowNumber, { [field]: checked }),
			}),
		[updateValues],
	);

	const removeRows = (rowNumbers) => {
		setRows((previous) => previous.filter(({ rowNumber }) => !rowNumbers.includes(rowNumber)));
		setSelection([]);
	};

	const handleRowUpdate = (newRow, oldRow) => {
		const changes = Object.fromEntries(
			USER_IMPORT_FIELDS.filter((field) => newRow[field] !== oldRow[field]).map((field) => [
				field,
				newRow[field],
			]),
		);
		if (Object.keys(changes).length) {
			updateValues(newRow.rowNumber, changes);
		}
		// The grid merges this into its copy of the row once the edit finishes. Only the edited
		// values: newRow still has the status, errors and warnings from before the edit, which
		// would replace the ones just checked
		return { rowNumber: newRow.rowNumber, ...changes };
	};

	const handleCancel = async () => {
		const confirmed = await confirm({
			title: 'Choose another file?',
			content: 'Your changes to these rows will be lost.',
			confirmText: 'Choose another file',
		});
		if (confirmed) {
			onCancel();
		}
	};

	const handleImport = async () => {
		const result = await startUserImport({
			storagePath: file.storagePath,
			fileName: file.fileName,
			rows,
			editedCount: rows.filter(({ rowNumber }) => editedRowNumbers.has(rowNumber)).length,
			removedCount: file.rows.length - rows.length,
		});
		if (result.data) {
			allowLeave();
			navigate(`${paths.admin}/imports/${result.data.id}`);
			return;
		}
		if (result.error.data?.rows) {
			setServerProblems(
				Object.fromEntries(result.error.data.rows.map((row) => [row.rowNumber, row])),
			);
		}
	};

	return (
		<Stack spacing={2}>
			<Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
				<Typography variant='form_label'>{file.fileName}</Typography>
				{isChecking && <CircularProgress size={18} />}
			</Stack>

			<Typography variant='body2' color='text.secondary'>
				{
					"Double-click a text cell, or click a dropdown cell, to change it, and hover over an outlined cell to see what's wrong. Rows with errors must be fixed or deleted before importing. Rows with warnings are imported, except those whose email is already a Folio user's or in an earlier row, which are skipped."
				}
			</Typography>
			<Box
				sx={{
					'& .import-review-error': {
						backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
						border: 1,
						borderColor: 'error.main',
					},
					'& .import-review-warning': {
						backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
						border: 1,
						borderColor: 'warning.main',
					},
					// The pickers in cells being edited sit flat in the cell: no outline or background
					'& .MuiDataGrid-cell--editing': {
						'& .MuiOutlinedInput-notchedOutline': { border: 'none' },
						'& .MuiInputBase-root': { backgroundColor: 'transparent' },
					},
				}}
			>
				<AdminDataGrid
					table='userImportReview'
					columns={columns}
					rows={gridRows}
					getRowId={(row) => row.rowNumber}
					objectType='rows'
					showColumnsButton={false}
					footerContent={<UserImportReviewSummary rows={checkedRows} />}
					toolbarActions={
						<Stack direction='row' spacing={1} sx={{ ml: 'auto' }}>
							{Boolean(selection.length) && (
								<Button
									variant='outlined'
									size='small'
									color='error'
									startIcon={<DeleteOutlineIcon />}
									onClick={() => removeRows(selection)}
								>
									{`Delete rows (${selection.length})`}
								</Button>
							)}
							{Boolean(existingUserRowNumbers.length) && (
								<Button
									variant='outlined'
									size='small'
									onClick={() => removeRows(existingUserRowNumbers)}
								>
									{`Remove rows already in Folio (${existingUserRowNumbers.length})`}
								</Button>
							)}
						</Stack>
					}
					height='60vh'
					maxHeight='60vh'
					initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
					checkboxSelection
					rowSelectionModel={selection}
					onRowSelectionModelChange={setSelection}
					// The broker group and Insight id columns edit on one click
					cellModesModel={cellModesModel}
					onCellModesModelChange={handleCellModesModelChange}
					onCellClick={handleCellClick}
					processRowUpdate={handleRowUpdate}
					onProcessRowUpdateError={(updateError) =>
						console.error('Could not update the import row', updateError)
					}
					// Picking from a broker group, Insight or role dropdown (outside the cell) keeps it
					// editing
					onCellEditStop={(params, event) => {
						if (
							params.reason === GridCellEditStopReasons.cellFocusOut &&
							event?.target?.closest?.('.MuiAutocomplete-popper, .MuiPopover-root')
						) {
							event.defaultMuiPrevented = true;
						}
					}}
				/>
			</Box>
			{error && (
				<Alert severity='error'>
					{error.data?.error ?? "Couldn't start the import. Please try again."}
				</Alert>
			)}
			<Stack direction='row' spacing={1}>
				<Tooltip title={importDisabledReason}>
					{/* A disabled button gets no hover events; the span does, for the tooltip */}
					<span>
						<Button
							variant='contained'
							onClick={handleImport}
							disabled={Boolean(importDisabledReason) || isStarting}
						>
							{isStarting ? (
								<CircularProgress size={18} color='inherit' />
							) : (
								`Import ${rows.length} users`
							)}
						</Button>
					</span>
				</Tooltip>
				<Button variant='outlined' onClick={handleCancel} disabled={isStarting}>
					Choose another file
				</Button>
			</Stack>
		</Stack>
	);
};

UserImportReview.displayName = 'UserImportReview';
